import { Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import CustomCopyButton from "../../../../../../components/CustomCopyButton";
import {
  AD_ACCOUNT_OPTIONS_DISPLAY_NAMES,
  ComplexEntityTypes,
} from "../../../../../../utils/constants";
import IntegrationImage from "../../../../integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../../../integrations/IntegrationsMapping";
import { useEntities } from "../../../hooks/useEntities";
import EntityDrawer from "../../EntityDrawer/EntityDrawer";
import TextIntegrationStyle from "../../TextIntegrationStyle";
import {
  checkIfUserAdmin,
  getUserConnectedHostsRows,
  getUserRelatedTokensRows,
} from "./checkRowsUserData";
import UserConnectedHostsView from "./UserConnectedHostsView";
import UserRelatedTokensView from "./UserRelatedTokensView";

const UserDetailsView = ({ entityData }) => {
  const [isInvitedByUserDrawerOpen, setIsInvitedByUserDrawerOpen] =
    useState(false);
  const { useQueryForEntitiesByType } = useEntities();
  const { trackEvent } = useAPI();

  const { data: userEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.USER,
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  const invitedByUserEntity = useMemo(() => {
    if (!userEntities || !entityData?.slack_saas_data?.invited_by) {
      return null;
    }

    return userEntities.find(
      (entity) => entity.unique_id === entityData.slack_saas_data.invited_by
    );
  }, [userEntities, entityData?.slack_saas_data?.invited_by]);

  const onInvitedByUserClick = () => {
    trackEvent(
      `User clicked on invited by user ${entityData?.slack_saas_data?.invited_by}, from user details view ${entityData?.display_name}`
    );

    if (invitedByUserEntity) {
      setIsInvitedByUserDrawerOpen(true);
    }
  };

  const getInvitedByUserDisplayName = () => {
    if (invitedByUserEntity) {
      return (
        invitedByUserEntity.display_name ||
        invitedByUserEntity.original_unique_id
      );
    }
    return entityData?.slack_saas_data?.invited_by;
  };

  return (
    <Box display={"flex"} flexDirection={"column"} gap={2}>
      {entityData?.id && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Email</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={""}
              value={entityData?.original_unique_id}
            ></TextIntegrationStyle>
            <CustomCopyButton
              copyTitle={"email"}
              copyText={entityData?.original_unique_id}
            />
          </Box>
        </Box>
      )}
      {entityData?.microsoft_active_directory_data?.organizational_unit && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Organizational Unit</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={""}
              value={
                entityData?.microsoft_active_directory_data?.organizational_unit
              }
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {/* USERNAME */}
      {(entityData?.servicenow_cmdb_data?.username ||
        entityData?.cyberark_identity_data?.username ||
        entityData?.microsoft_active_directory_data?.ad_user_name) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Username</Typography>
          </Box>
          {entityData?.servicenow_cmdb_data?.username && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"servicenow_cmdb"}
                      width={"16px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.servicenow_cmdb_data?.username}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.cyberark_identity_data?.username && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.cyberark_identity_data?.username}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.microsoft_active_directory_data?.ad_user_name && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={
                  entityData?.microsoft_active_directory_data?.ad_user_name
                }
              ></TextIntegrationStyle>
            </Box>
          )}
        </Box>
      )}
      {/* TITLE */}
      {(entityData?.hibob_data?.title ||
        entityData?.bamboohr_data?.title ||
        entityData?.slack_saas_data?.title ||
        entityData?.box_data?.job_title ||
        entityData?.addigy_data?.addigy_role ||
        entityData?.kandji_data?.job_title ||
        entityData?.monday_data?.title ||
        entityData?.salesforce_data?.title) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Title</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={""}
              value={entityData?.hibob_data?.title}
            ></TextIntegrationStyle>
          </Box>
          {entityData?.bamboohr_data?.title && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.bamboohr_data?.title}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.slack_saas_data?.title && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"slack"} width={"30px"} />
                  </Box>
                }
                value={entityData?.slack_saas_data?.title}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.box_data?.job_title && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"box"} width={"30px"} />
                  </Box>
                }
                value={entityData?.box_data?.job_title}
              />
            </Box>
          )}
          {entityData?.addigy_data?.addigy_role && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.addigy_data?.addigy_role}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.kandji_data?.job_title && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"kandji"} width={"30px"} />
                  </Box>
                }
                value={entityData?.kandji_data?.job_title}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.monday_data?.title && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"monday"} width={"30px"} />
                  </Box>
                }
                value={entityData?.monday_data?.title}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.salesforce_data?.title && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={IntegrationsMapping.salesforce.codeName}
                      width={"30px"}
                    />
                  </Box>
                }
                value={entityData.salesforce_data.title}
              />
            </Box>
          )}
        </Box>
      )}
      {/* DEPARTMENT */}
      {(entityData?.hibob_data?.department ||
        entityData?.bamboohr_data?.department ||
        entityData?.kandji_data?.department) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Department</Typography>
          </Box>
          {entityData?.hibob_data?.department && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.hibob_data?.department}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.bamboohr_data?.department && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.bamboohr_data?.department}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.kandji_data?.department && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"kandji"} width={"30px"} />
                  </Box>
                }
                value={entityData?.kandji_data?.department}
              ></TextIntegrationStyle>
            </Box>
          )}
        </Box>
      )}
      {/* SITE */}
      {(entityData?.hibob_data?.site || entityData?.bamboohr_data?.site) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Site</Typography>
          </Box>
          {entityData?.hibob_data?.site && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.hibob_data?.site}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.bamboohr_data?.site && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={""}
                value={entityData?.bamboohr_data?.site}
              ></TextIntegrationStyle>
            </Box>
          )}
        </Box>
      )}
      {/* ADMIN */}
      {checkIfUserAdmin(entityData) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant="subtitle2">Admin In</Typography>
          </Box>
          <Box display={"flex"} flexDirection={"row"} ml={2}>
            {Object.entries(entityData?.admin_roles || {}).map(([platform]) => (
              <IntegrationImage key={platform} name={platform} width={"30px"} />
            ))}
          </Box>
        </Box>
      )}
      {/* USER TYPE */}
      {((entityData?.bitwarden_data?.user_type &&
        entityData?.bitwarden_data?.user_type !== "User") ||
        entityData?.microsoft_office_365_data?.user_type ||
        entityData?.microsoft_entra_id_data?.user_type ||
        entityData?.snowflake_data?.type ||
        entityData?.ninjaone_data?.user_type ||
        entityData?.monday_data?.user_type ||
        entityData?.slack_saas_data?.user_type) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>User Type</Typography>
          </Box>
          {entityData?.bitwarden_data?.user_type && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"bitwarden"} width={"30px"} />
                  </Box>
                }
                value={entityData?.bitwarden_data?.user_type}
              />
            </Box>
          )}
          {entityData?.microsoft_office_365_data?.user_type && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"microsoft_office_365"}
                      width={"30px"}
                    />
                  </Box>
                }
                value={entityData?.microsoft_office_365_data?.user_type}
              />
            </Box>
          )}
          {entityData?.microsoft_entra_id_data?.user_type && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"microsoft_entra_id"}
                      width={"30px"}
                    />
                  </Box>
                }
                value={entityData?.microsoft_entra_id_data?.user_type}
              />
            </Box>
          )}
          {entityData?.snowflake_data?.type && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"snowflake"} width={"30px"} />
                  </Box>
                }
                value={entityData.snowflake_data.type}
              />
            </Box>
          )}
          {entityData?.ninjaone_data?.user_type && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"ninjaone"} width={"30px"} />
                  </Box>
                }
                value={entityData?.ninjaone_data?.user_type}
              />
            </Box>
          )}
          {entityData?.monday_data?.user_type && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"monday"} width={"30px"} />
                  </Box>
                }
                value={entityData?.monday_data?.user_type}
              ></TextIntegrationStyle>
            </Box>
          )}
          {entityData?.slack_saas_data?.user_type && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"slack_saas"} width={"30px"} />
                  </Box>
                }
                value={entityData?.slack_saas_data?.user_type}
              ></TextIntegrationStyle>
            </Box>
          )}
        </Box>
      )}
      {/* STATUS */}
      {(entityData?.hibob_data?.status ||
        entityData?.bitwarden_data?.user_status ||
        entityData?.snowflake_data?.status ||
        entityData?.bamboohr_data?.status ||
        entityData?.atlassian_data?.account_status ||
        entityData?.okta_data?.status ||
        entityData?.zoom_data?.status ||
        entityData?.netskope_data?.status ||
        entityData?.box_data?.status ||
        entityData?.cyberark_identity_data?.status) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Status</Typography>
          </Box>
          <Box
            display={"flex"}
            gap={1}
            marginLeft={"22px"}
            flexDirection={"column"}
          >
            {entityData?.hibob_data?.status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"hibob"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={`${entityData.hibob_data.status}${
                  entityData?.hibob_data?.lifecycle_status
                    ? ` - ${entityData.hibob_data.lifecycle_status}`
                    : ""
                }`}
              />
            )}
            {entityData?.bitwarden_data?.user_status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"bitwarden"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.bitwarden_data?.user_status}
              />
            )}
            {entityData?.snowflake_data?.status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"snowflake"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData.snowflake_data.status}
              />
            )}
            {entityData?.bamboohr_data?.status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"bamboohr"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={`${entityData?.bamboohr_data?.status}`}
              />
            )}
            {entityData?.atlassian_data?.account_status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"atlassian"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.atlassian_data?.account_status}
              />
            )}
            {entityData?.okta_data?.status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"okta"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.okta_data?.status}
              />
            )}
            {entityData?.zoom_data?.status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"zoom"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.zoom_data?.status}
              />
            )}
            {entityData?.netskope_data?.status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"netskope"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.netskope_data?.status}
              />
            )}
            {entityData?.box_data?.status && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage
                      name={"box"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.box_data?.status}
              />
            )}
            {entityData?.cyberark_identity_data?.status && (
              <TextIntegrationStyle
                title={""}
                value={entityData?.cyberark_identity_data?.status}
              ></TextIntegrationStyle>
            )}
          </Box>
        </Box>
      )}
      {entityData?.microsoft_active_directory_data?.user_account_control_flags
        ?.length > 0 && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" gap={1} alignItems="center">
            <Typography variant="subtitle2">AD Account Options</Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={1} ml={3}>
            {entityData?.microsoft_active_directory_data?.user_account_control_flags.map(
              (flag) => (
                <TextIntegrationStyle
                  key={flag}
                  title={""}
                  value={AD_ACCOUNT_OPTIONS_DISPLAY_NAMES[flag] || flag}
                />
              )
            )}
          </Box>
        </Box>
      )}
      {/* PROJECTS */}
      {entityData?.gitlab_data?.projects &&
        entityData?.gitlab_data?.projects.length > 0 && (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="subtitle2">Projects</Typography>
            </Box>
            <Box display="flex" flexDirection="column" gap={1} ml={3}>
              {entityData?.gitlab_data?.projects.map((project) => (
                <Typography key={project} variant={"caption"}>
                  {project}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      {/* TERMINATION DATE */}
      {entityData?.hibob_data?.termination_date && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Termination Date</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={
                <Box display={"flex"}>
                  <IntegrationImage name={"hibob"} width={"40px"} />
                </Box>
              }
              value={formatDate(
                entityData?.hibob_data?.termination_date
                  ?.timestamp_utc_human_readable
              )}
            />
          </Box>
        </Box>
      )}
      {entityData?.bamboohr_data?.termination_date && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Termination Date</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={
                <Box display={"flex"}>
                  <IntegrationImage name={"bamboohr"} width={"40px"} />
                </Box>
              }
              value={formatDate(
                entityData?.bamboohr_data?.termination_date
                  ?.timestamp_utc_human_readable
              )}
            />
          </Box>
        </Box>
      )}
      {entityData?.bamboohr_data?.timeoff_start_date && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Time Off Start Date</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={
                <Box display={"flex"}>
                  <IntegrationImage name={"bamboohr"} width={"40px"} />
                </Box>
              }
              value={formatDate(
                entityData?.bamboohr_data?.timeoff_start_date
                  ?.timestamp_utc_human_readable
              )}
            />
          </Box>
        </Box>
      )}
      {entityData?.bamboohr_data?.timeoff_end_date && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Time Off End Date</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={
                <Box display={"flex"}>
                  <IntegrationImage name={"bamboohr"} width={"40px"} />
                </Box>
              }
              value={formatDate(
                entityData?.bamboohr_data?.timeoff_end_date
                  ?.timestamp_utc_human_readable
              )}
            />
          </Box>
        </Box>
      )}
      {/* CONNECTED HOSTS */}
      {getUserConnectedHostsRows(entityData).length > 0 && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant="subtitle2">Connected Hosts</Typography>
          </Box>
          <Box display={"flex"} gap={1} ml={2}>
            <UserConnectedHostsView entityData={entityData} />
          </Box>
        </Box>
      )}
      {/* RELATED TOKENS */}
      {getUserRelatedTokensRows(entityData).length > 0 && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant="subtitle2">Related Tokens</Typography>
          </Box>
          <Box display={"flex"} gap={1} ml={2}>
            <UserRelatedTokensView entityData={entityData} />
          </Box>
        </Box>
      )}
      {/* ORGANIZATIONAL UNIT */}
      {entityData?.google_data?.org_unit && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Organizational Unit</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            {entityData?.google_data?.org_unit && (
              <IntegrationImage name={"google"} width={"30px"} />
            )}
            <TextIntegrationStyle
              title={""}
              value={entityData?.google_data?.org_unit}
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {/* ORGANIZATION */}
      {entityData?.aws_data?.org && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Organization</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            {entityData?.aws_data?.org && (
              <IntegrationImage name={"aws"} width={"30px"} />
            )}
            <TextIntegrationStyle
              title={""}
              value={entityData?.aws_data?.org}
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {/* LOGIN NAMES */}
      {entityData?.snowflake_data?.login_names?.length > 0 && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography variant="subtitle2">Login Names</Typography>
          </Box>
          <Box
            ml={2}
            display="flex"
            alignItems="center"
            gap={0.5}
            marginLeft={"22px"}
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              {entityData?.snowflake_data?.login_names.map(
                (login_name, index) => (
                  <TextIntegrationStyle
                    key={index}
                    title={
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        alignItems={"center"}
                      >
                        <IntegrationImage
                          name={"snowflake"}
                          width={"18px"}
                          newLogo={true}
                        />
                      </Box>
                    }
                    value={login_name}
                  ></TextIntegrationStyle>
                )
              )}
            </Box>
          </Box>
        </Box>
      )}
      {/* AUTHENTICATION PROVIDER */}
      {(entityData?.paloalto_talon_data?.authentication ||
        entityData?.bitwarden_data?.is_user_with_sso ||
        entityData?.zoom_data?.login_types ||
        entityData?.cato_data?.vpn_auth_method ||
        entityData?.cloudconnexa_open_vpn_data?.auth_type ||
        entityData?.salesforce_data ||
        entityData?.paloalto_cortex_xdr_data?.authentication_provider) && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>
              Authentication Provider
            </Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2} flexDirection={"column"}>
            {entityData?.paloalto_talon_data?.authentication && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={"paloalto_talon"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.paloalto_talon_data?.authentication}
              />
            )}
            {entityData?.paloalto_prisma_access_browser_data
              ?.authentication && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={"paloalto_prisma_access_browser"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={
                  entityData?.paloalto_prisma_access_browser_data
                    ?.authentication
                }
              />
            )}
            {entityData?.bitwarden_data?.is_user_with_sso && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={"bitwarden"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.bitwarden_data?.is_user_with_sso}
              />
            )}
            {entityData?.zoom_data?.login_types && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={"zoom"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.zoom_data?.login_types.join(", ")}
              />
            )}
            {entityData?.cato_data?.vpn_auth_method && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={"cato"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.cato_data?.vpn_auth_method}
              />
            )}
            {entityData?.cloudconnexa_open_vpn_data?.auth_type && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={"cloudconnexa_open_vpn"}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={entityData?.cloudconnexa_open_vpn_data?.auth_type}
              />
            )}
            {entityData?.salesforce_data && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={IntegrationsMapping.salesforce.codeName}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={
                  entityData?.salesforce_data?.is_sso_enabled ? "SSO" : "Local"
                }
              />
            )}
            {entityData?.paloalto_cortex_xdr_data?.authentication_provider && (
              <TextIntegrationStyle
                title={
                  <Box display={"flex"} paddingLeft={"6px"}>
                    <IntegrationImage
                      name={IntegrationsMapping.paloalto_cortex_xdr.codeName}
                      width={"18px"}
                      newLogo={true}
                    />
                  </Box>
                }
                value={
                  entityData?.paloalto_cortex_xdr_data.authentication_provider
                }
              />
            )}
          </Box>
        </Box>
      )}
      {/* EXTENSION USAGE */}
      {entityData?.paloalto_talon_data?.extensions && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Extension Usage</Typography>
          </Box>
          <Box display={"flex"} flexDirection={"column"}>
            {Array.from(
              new Set(
                entityData?.paloalto_talon_data?.extensions.map(
                  (extensionBrowser) => extensionBrowser.browser
                )
              )
            ).map((browser) => (
              <Box display={"flex"} gap={1} ml={2} key={browser}>
                <IntegrationImage name={"paloalto_talon"} width={"24px"} />
                <TextIntegrationStyle
                  title={""}
                  value={browser}
                ></TextIntegrationStyle>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {entityData?.paloalto_prisma_access_browser_data?.extensions && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Extension Usage</Typography>
          </Box>
          <Box display={"flex"} flexDirection={"column"}>
            {Array.from(
              new Set(
                entityData?.paloalto_prisma_access_browser_data?.extensions.map(
                  (extensionBrowser) => extensionBrowser.browser
                )
              )
            ).map((browser) => (
              <Box display={"flex"} gap={1} ml={2} key={browser}>
                <IntegrationImage
                  name={"paloalto_prisma_access_browser"}
                  width={"24px"}
                />
                <TextIntegrationStyle
                  title={""}
                  value={browser}
                ></TextIntegrationStyle>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {entityData?.microsoft_active_directory_data?.logon_workstation && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Logon Workstation</Typography>
          </Box>
          <Box display={"flex"} gap={2} ml={2}>
            <TextIntegrationStyle
              title={""}
              value={
                entityData?.microsoft_active_directory_data?.logon_workstation
              }
            ></TextIntegrationStyle>
          </Box>
        </Box>
      )}
      {entityData?.microsoft_active_directory_data?.allowed_to_delegate_to
        ?.length > 0 && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>
              Constrained Delegation Service
            </Typography>
          </Box>
          <Box display={"flex"} flexDirection={"column"} gap={1} ml={2}>
            {entityData?.microsoft_active_directory_data?.allowed_to_delegate_to?.map(
              (item, idx) => (
                <TextIntegrationStyle key={idx} title={""} value={item} />
              )
            )}
          </Box>
        </Box>
      )}
      {entityData?.microsoft_active_directory_data?.service_principal_names
        ?.length > 0 && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>
              Service Principal Name
            </Typography>
          </Box>
          <Box display={"flex"} flexDirection={"column"} gap={1} ml={2}>
            {entityData?.microsoft_active_directory_data?.service_principal_names?.map(
              (item, idx) => (
                <TextIntegrationStyle key={idx} title={""} value={item} />
              )
            )}
          </Box>
        </Box>
      )}
      {/* INVITED BY */}
      {entityData?.slack_saas_data?.invited_by && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Box display={"flex"} gap={1} alignItems={"center"}>
            <Typography variant={"subtitle2"}>Invited By</Typography>
          </Box>
          {entityData?.slack_saas_data?.invited_by && (
            <Box display={"flex"} gap={2} ml={2}>
              <TextIntegrationStyle
                title={
                  <Box display={"flex"}>
                    <IntegrationImage name={"slack_saas"} width={"30px"} />
                  </Box>
                }
                value={getInvitedByUserDisplayName()}
                onClick={invitedByUserEntity ? onInvitedByUserClick : undefined}
                style={
                  invitedByUserEntity
                    ? {
                        cursor: "pointer",
                        textDecoration: "underline",
                      }
                    : {}
                }
                tooltipTitle={
                  invitedByUserEntity ? "Click to view user details" : undefined
                }
              ></TextIntegrationStyle>
            </Box>
          )}
        </Box>
      )}
      <EntityDrawer
        openDrawer={isInvitedByUserDrawerOpen && invitedByUserEntity}
        selectedEntity={invitedByUserEntity}
        onClose={() => {
          setIsInvitedByUserDrawerOpen(false);
        }}
      />
    </Box>
  );
};

export default UserDetailsView;
